// apps/web/src/components/visitors/VisitorCard/VisitorQRDialog.tsx
import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Alert,
  Button,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Share as ShareIcon,
  Image as ImageIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import QRCode from 'qrcode';

interface VisitorQRDialogProps {
  open: boolean;
  onClose: () => void;
  visitorName: string;
  qrData: string; // JSON строка без timestamp
}

// Кэш для сгенерированных Blob по ключу (visitorId)
const blobCache = new Map<string, Blob>();

const VisitorQRDialog: React.FC<VisitorQRDialogProps> = ({
  open,
  onClose,
  visitorName,
  qrData,
}) => {
  const [copying, setCopying] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  // Извлекаем visitorId из qrData для ключа кэша
  const getVisitorId = useCallback((): string => {
    try {
      const parsed = JSON.parse(qrData);
      return parsed.id || 'unknown';
    } catch {
      return 'unknown';
    }
  }, [qrData]);

  const visitorId = getVisitorId();

  // Сбрасываем состояния при каждом открытии
  useEffect(() => {
    if (open) {
      setCopying(false);
      setDownloading(false);
      setSharing(false);
      setSnackbar({ open: false, message: '' });
    }
  }, [open]);

  // Генерация PNG Blob с кэшированием
  const generatePNGBlob = useCallback((): Promise<Blob> => {
    // Проверяем кэш
    const cached = blobCache.get(visitorId);
    if (cached) {
      return Promise.resolve(cached);
    }

    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      canvas.width = 400;
      canvas.height = 400;

      QRCode.toCanvas(
        canvas,
        qrData,
        {
          width: 400,
          margin: 2,
          errorCorrectionLevel: 'H',
          color: {
            dark: '#000000',
            light: '#ffffff',
          },
        },
        (error) => {
          if (error) {
            reject(error);
            return;
          }
          canvas.toBlob((blob) => {
            if (blob) {
              blobCache.set(visitorId, blob);
              resolve(blob);
            } else {
              reject(new Error('Failed to create blob'));
            }
          }, 'image/png');
        }
      );
    });
  }, [qrData, visitorId]);

  const handleCopyImage = useCallback(async () => {
    setCopying(true);
    try {
      const blob = await generatePNGBlob();
      await navigator.clipboard.write([
        new ClipboardItem({
          [blob.type]: blob,
        }),
      ]);
      setSnackbar({ open: true, message: 'QR-код скопирован как изображение' });
    } catch {
      setSnackbar({
        open: true,
        message: 'Не удалось скопировать. Используйте скачивание.',
      });
    } finally {
      setCopying(false);
    }
  }, [generatePNGBlob]);

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    try {
      const blob = await generatePNGBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `qr-${visitorName || 'visitor'}.png`;
      link.click();
      URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: 'QR-код сохранён' });
    } catch {
      setSnackbar({ open: true, message: 'Не удалось сохранить' });
    } finally {
      setDownloading(false);
    }
  }, [generatePNGBlob, visitorName]);

  const handleShare = useCallback(async () => {
    if (!navigator.share) {
      handleCopyImage();
      return;
    }

    setSharing(true);
    try {
      const blob = await generatePNGBlob();
      const file = new File([blob], `qr-${visitorName}.png`, { type: 'image/png' });
      await navigator.share({
        title: `QR-код: ${visitorName}`,
        text: `QR-код посетителя ${visitorName}`,
        files: [file],
      });
    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        setSnackbar({ open: true, message: 'Не удалось поделиться' });
      }
    } finally {
      setSharing(false);
    }
  }, [generatePNGBlob, visitorName, handleCopyImage]);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">QR-код: {visitorName}</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" gap={2} py={1}>
            <Box
              sx={{
                width: '100%',
                maxWidth: 280,
                aspectRatio: '1/1',
                bgcolor: 'white',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              <QRCodeSVG
                value={qrData}
                size={280}
                level="H"
                includeMargin={true}
                style={{ width: '100%', height: '100%' }}
              />
            </Box>

            <Box display="flex" gap={1} flexWrap="wrap" justifyContent="center">
              <Button
                variant="outlined"
                size="small"
                disabled={copying || downloading || sharing}
                startIcon={copying ? <CircularProgress size={16} color="inherit" /> : <ImageIcon />}
                onClick={handleCopyImage}
              >
                {copying ? 'Копирование...' : 'Копировать QR'}
              </Button>

              <Button
                variant="outlined"
                size="small"
                disabled={copying || downloading || sharing}
                startIcon={
                  downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />
                }
                onClick={handleDownload}
              >
                {downloading ? 'Сохранение...' : 'Скачать PNG'}
              </Button>

              <Button
                variant="outlined"
                size="small"
                disabled={copying || downloading || sharing}
                startIcon={sharing ? <CircularProgress size={16} color="inherit" /> : <ShareIcon />}
                onClick={handleShare}
              >
                {sharing ? 'Отправка...' : 'Поделиться'}
              </Button>
            </Box>

            <Alert severity="info" sx={{ width: '100%', fontSize: '0.85rem' }}>
              QR-код генерируется один раз и кэшируется. При повторных операциях для того же
              посетителя копирование происходит мгновенно.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={onClose} variant="contained">
            Готово
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default VisitorQRDialog;
